import React, { useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/Offers.css';

interface Offer {
  _id: string;
  code: string;
  title: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  minOrderValue?: number;
  maxDiscountAmount?: number;
  conditions: {
    type: 'first_time_user' | 'min_orders_completed' | 'all_users';
    value?: number;
  };
  validFrom: string;
  validUntil: string;
  isActive: boolean;
  usageLimit?: number;
  usageCount: number;
  createdAt: string;
}

const Offers: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: 0,
    minOrderValue: 0,
    maxDiscountAmount: 0,
    conditionType: 'all_users' as 'first_time_user' | 'min_orders_completed' | 'all_users',
    conditionValue: 0,
    validFrom: '',
    validUntil: '',
    usageLimit: 0,
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setIsLoading(true);
    try {
      const data = await api.getOffers();
      setOffers(data.offers || []);
      setError('');
    } catch (err: any) {
      setError('Failed to load offers');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (offer?: Offer) => {
    if (offer) {
      setEditingOffer(offer);
      setFormData({
        code: offer.code,
        title: offer.title,
        description: offer.description,
        discountType: offer.discountType,
        discountValue: offer.discountValue,
        minOrderValue: offer.minOrderValue || 0,
        maxDiscountAmount: offer.maxDiscountAmount || 0,
        conditionType: offer.conditions.type,
        conditionValue: offer.conditions.value || 0,
        validFrom: offer.validFrom.split('T')[0],
        validUntil: offer.validUntil.split('T')[0],
        usageLimit: offer.usageLimit || 0,
      });
    } else {
      setEditingOffer(null);
      setFormData({
        code: '',
        title: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        minOrderValue: 0,
        maxDiscountAmount: 0,
        conditionType: 'all_users',
        conditionValue: 0,
        validFrom: '',
        validUntil: '',
        usageLimit: 0,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOffer(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const offerData = {
      code: formData.code.toUpperCase(),
      title: formData.title,
      description: formData.description,
      discountType: formData.discountType,
      discountValue: Number(formData.discountValue),
      minOrderValue: formData.minOrderValue > 0 ? Number(formData.minOrderValue) : undefined,
      maxDiscountAmount: formData.maxDiscountAmount > 0 ? Number(formData.maxDiscountAmount) : undefined,
      conditions: {
        type: formData.conditionType,
        value: formData.conditionType === 'min_orders_completed' ? Number(formData.conditionValue) : undefined,
      },
      validFrom: formData.validFrom,
      validUntil: formData.validUntil,
      usageLimit: formData.usageLimit > 0 ? Number(formData.usageLimit) : undefined,
    };

    try {
      if (editingOffer) {
        await api.updateOffer(editingOffer._id, offerData);
      } else {
        await api.createOffer(offerData);
      }
      fetchOffers();
      handleCloseModal();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save offer');
    }
  };

  const handleToggleStatus = async (offerId: string, currentStatus: boolean) => {
    try {
      await api.toggleOfferStatus(offerId, !currentStatus);
      fetchOffers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update offer status');
    }
  };

  const handleDeleteOffer = async (offerId: string) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    try {
      await api.deleteOffer(offerId);
      fetchOffers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete offer');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getConditionText = (offer: Offer) => {
    switch (offer.conditions.type) {
      case 'first_time_user':
        return 'First time users only';
      case 'min_orders_completed':
        return `Users with ${offer.conditions.value}+ completed orders`;
      case 'all_users':
        return 'All users';
      default:
        return 'Unknown';
    }
  };

  const filteredOffers = offers.filter(offer => {
    if (filterStatus === 'active') return offer.isActive;
    if (filterStatus === 'inactive') return !offer.isActive;
    return true;
  });

  if (isLoading) {
    return (
      <div className="offers-page">
        <div className="loading">Loading offers...</div>
      </div>
    );
  }

  return (
    <div className="offers-page">
      <div className="page-header">
        <div>
          <h1>Promotional Offers</h1>
          <p>Manage discount codes and promotional campaigns</p>
        </div>
        <button className="btn-create" onClick={() => handleOpenModal()}>
          ➕ Create Offer
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Filter */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All Offers</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
        <div className="results-count">
          {filteredOffers.length} offer{filteredOffers.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Offers Grid */}
      <div className="offers-grid">
        {filteredOffers.map((offer) => (
          <div key={offer._id} className={`offer-card ${!offer.isActive ? 'inactive' : ''}`}>
            <div className="offer-header">
              <div className="offer-code-badge">{offer.code}</div>
              <div className="offer-status">
                <span className={`status-badge ${offer.isActive ? 'active' : 'inactive'}`}>
                  {offer.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <h3 className="offer-title">{offer.title}</h3>
            <p className="offer-description">{offer.description}</p>

            <div className="offer-details">
              <div className="detail-row">
                <span className="label">Discount:</span>
                <span className="value discount-value">
                  {offer.discountType === 'percentage'
                    ? `${offer.discountValue}% off`
                    : `₹${offer.discountValue} off`}
                </span>
              </div>

              {offer.minOrderValue && (
                <div className="detail-row">
                  <span className="label">Min Order:</span>
                  <span className="value">₹{offer.minOrderValue}</span>
                </div>
              )}

              {offer.maxDiscountAmount && (
                <div className="detail-row">
                  <span className="label">Max Discount:</span>
                  <span className="value">₹{offer.maxDiscountAmount}</span>
                </div>
              )}

              <div className="detail-row">
                <span className="label">Condition:</span>
                <span className="value">{getConditionText(offer)}</span>
              </div>

              <div className="detail-row">
                <span className="label">Valid:</span>
                <span className="value">
                  {formatDate(offer.validFrom)} - {formatDate(offer.validUntil)}
                </span>
              </div>

              {offer.usageLimit && (
                <div className="detail-row">
                  <span className="label">Usage:</span>
                  <span className="value">
                    {offer.usageCount} / {offer.usageLimit}
                  </span>
                </div>
              )}
            </div>

            <div className="offer-actions">
              <button onClick={() => handleOpenModal(offer)} className="btn-edit">
                ✏️ Edit
              </button>
              <button
                onClick={() => handleToggleStatus(offer._id, offer.isActive)}
                className={offer.isActive ? 'btn-deactivate' : 'btn-activate'}
              >
                {offer.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => handleDeleteOffer(offer._id)} className="btn-delete">
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredOffers.length === 0 && (
        <div className="empty-state">
          <p>No offers found</p>
          <button onClick={() => handleOpenModal()} className="btn-create-empty">
            Create your first offer
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingOffer ? 'Edit Offer' : 'Create New Offer'}</h2>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="offer-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Offer Code <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., FIRST50"
                    required
                  />
                  <small className="help-text">Unique code users will enter</small>
                </div>

                <div className="form-group">
                  <label>Discount Type <span className="required">*</span></label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as any })}
                    required
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Discount Value <span className="required">*</span></label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                    placeholder={formData.discountType === 'percentage' ? '10' : '100'}
                    required
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Title <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., First Order Discount"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description <span className="required">*</span></label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the offer..."
                  required
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Min Order Value (Optional)</label>
                  <input
                    type="number"
                    value={formData.minOrderValue}
                    onChange={(e) => setFormData({ ...formData, minOrderValue: Number(e.target.value) })}
                    placeholder="0"
                    min="0"
                  />
                  <small className="help-text">Minimum order value required</small>
                </div>

                <div className="form-group">
                  <label>Max Discount Amount (Optional)</label>
                  <input
                    type="number"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => setFormData({ ...formData, maxDiscountAmount: Number(e.target.value) })}
                    placeholder="0"
                    min="0"
                  />
                  <small className="help-text">Cap for percentage discounts</small>
                </div>
              </div>

              <div className="form-group">
                <label>User Condition <span className="required">*</span></label>
                <select
                  value={formData.conditionType}
                  onChange={(e) => setFormData({ ...formData, conditionType: e.target.value as any })}
                  required
                >
                  <option value="all_users">All Users</option>
                  <option value="first_time_user">First Time Users Only</option>
                  <option value="min_orders_completed">Users with Minimum Orders</option>
                </select>
              </div>

              {formData.conditionType === 'min_orders_completed' && (
                <div className="form-group">
                  <label>Minimum Orders Required <span className="required">*</span></label>
                  <input
                    type="number"
                    value={formData.conditionValue}
                    onChange={(e) => setFormData({ ...formData, conditionValue: Number(e.target.value) })}
                    placeholder="5"
                    required
                    min="1"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Valid From <span className="required">*</span></label>
                  <input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Valid Until <span className="required">*</span></label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Usage Limit (Optional)</label>
                <input
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: Number(e.target.value) })}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="help-text">Total number of times this offer can be used</small>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCloseModal} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingOffer ? 'Update Offer' : 'Create Offer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offers;
